interface BoardPaginationProps {
  currentPage: number; // 1-indexed
  totalPages: number;
  onChange: (page: number) => void;
  /** 양 옆에 몇 개씩 표시할지 */
  siblingCount?: number;
}

/**
 * 단순 페이지네이션.
 * 페이지 수가 많을 때 ... 으로 압축. siblingCount 양쪽 갯수.
 */
export function BoardPagination({
  currentPage,
  totalPages,
  onChange,
  siblingCount = 1,
}: BoardPaginationProps) {
  if (totalPages <= 1) return null;

  const pages = buildPageList(currentPage, totalPages, siblingCount);

  const baseBtn =
    'inline-flex h-10 min-w-10 items-center justify-center rounded-full px-3 text-[14px] font-medium transition-colors';
  const inactive = 'text-neutral-950/65 dark:text-white/65 hover:bg-black/[0.05] dark:hover:bg-white/10 hover:text-neutral-950 dark:hover:text-white';
  const active = 'bg-neutral-950 text-white dark:bg-white dark:text-neutral-950';
  const disabled = 'pointer-events-none opacity-30';

  return (
    <nav aria-label="Pagination" className="mt-10 flex items-center justify-center gap-1">
      <button
        type="button"
        onClick={() => onChange(currentPage - 1)}
        className={`${baseBtn} ${inactive} ${currentPage === 1 ? disabled : ''}`}
        aria-label="이전 페이지"
      >
        ←
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`dot-${i}`} className="px-2 text-neutral-950/40 dark:text-white/40">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`${baseBtn} ${p === currentPage ? active : inactive}`}
            aria-current={p === currentPage ? 'page' : undefined}
          >
            {p}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(currentPage + 1)}
        className={`${baseBtn} ${inactive} ${currentPage === totalPages ? disabled : ''}`}
        aria-label="다음 페이지"
      >
        →
      </button>
    </nav>
  );
}

/** 1, 2, 3, …, N 형태로 페이지 리스트 빌드 */
function buildPageList(
  current: number,
  total: number,
  sibling: number,
): (number | '…')[] {
  const result: (number | '…')[] = [];
  const left = Math.max(2, current - sibling);
  const right = Math.min(total - 1, current + sibling);

  result.push(1);
  if (left > 2) result.push('…');
  for (let i = left; i <= right; i++) result.push(i);
  if (right < total - 1) result.push('…');
  if (total > 1) result.push(total);

  return result;
}
